import { decodeParticipantState } from '@hub/common';
import {
  callProseGetScheduleName,
  callProseParticipantSchedule,
} from 'features/questionnaires/services/proseService';
import { hashParticipantState } from '../logic/hashParticipantState';
import { localDayBucket } from '../logic/localDay';
import type { Activity } from '@castoredc/castor_prose';

type ComputeArgs = {
  dslSource: string;
  participationId: string; // cache identity — one entry per participation
  definitionKey: string; // manifest.definition (the cache identity for the DSL)
  participantState: string; // base64
  timezone: string; // IANA id
  now: Date;
};

// One memo entry per participation, keyed by participationId, so switching
// between participations does not evict the other's entry. The stored `key`
// folds in the DSL/state-hash/timezone/day-bucket, so a participation's own
// entry is still invalidated when any of those change.
const cache = new Map<string, { key: string; activities: Activity[] }>();

// Test-only cache reset.
export const __resetScheduleCache = () => {
  cache.clear();
};

export const computeParticipantSchedule = async ({
  dslSource,
  participationId,
  definitionKey,
  participantState,
  timezone,
  now,
}: ComputeArgs): Promise<Activity[]> => {
  // Fold the local-day bucket into the cache key as a daily freshness backstop:
  // the projection otherwise changes only with the DSL, state, or timezone.
  const dayBucket = String(localDayBucket(now));
  const key = `${participationId} ${definitionKey} ${hashParticipantState(participantState)} ${timezone} ${dayBucket}`;
  const cached = cache.get(participationId);
  if (cached?.key === key) return cached.activities;

  const scheduleName = await callProseGetScheduleName(dslSource);
  const response = await callProseParticipantSchedule(dslSource, scheduleName, {
    state: decodeParticipantState(participantState),
    timezone: { id: timezone },
    // Project from the epoch: PROSE prunes an occurrence when it STARTS before
    // the range beginning, even while its availability window is still open
    // (pinned in participantScheduleRange.prose.test.ts). A day-anchored
    // beginning therefore hides still-actionable tasks whose window opened on
    // an earlier day. Display pruning is the client's job: classifyAvailability
    // marks past windows 'expired' and isListableTask hides them.
    range: {
      rangeKind: {
        $case: 'timestampRange',
        value: { beginning: { seconds: 0 } },
      },
    },
  });

  if (typeof response === 'string') throw new Error(response);

  cache.set(participationId, { key, activities: response.activities });
  return response.activities;
};
